/* eslint-disable max-len */
import { createElement } from 'react';
import { alpha, createTheme } from '@mui/material/styles';

import { CheckboxCheckedIcon, CheckboxIndeterminateIcon, CheckboxUncheckedIcon } from './checkboxIcons';
import core from './_core';

const blankTheme = createTheme({});

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      orange: string;
      red: string;
    };
  }
  interface PaletteOptions {
    custom: {
      orange: string;
      red: string;
    };
  }
}

export default createTheme({
  palette: {
    primary: {
      main: '#464af0',
    },
    secondary: {
      main: '#F2AF0D', // TODO change it
    },
    warning: {
      main: '#ff0000',
    },
    text: {
      secondary: 'rgba(0, 0, 0, 0.7)',
    },
    background: {
      // Match previous app chrome (GlobalCss used grey[100]); v5 CssBaseline reads this.
      default: '#f5f5f5',
      paper: '#fff',
    },
    custom: {
      orange: '#F2AF0D',
      red: '#FF0000',
    },
  },
  typography: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: 16,
    button: {
      textTransform: 'none',
    },
    h1: {
      color: '#000000',
      fontSize: '2rem',
      fontWeight: 900,
    },
    h2: {
      color: '#464AF0',
      fontSize: '1.5rem', // 24px
      fontWeight: 900,
    },
    h3: {
      color: '#464AF0',
      fontSize: '1.25rem', // 20px
      fontWeight: 900,
    },
    h4: {
      color: '#000000',
      fontSize: '1rem', // 16px
      fontWeight: 700,
    },
    h5: {
      color: '#464AF0',
      fontSize: '1rem', // 16px
      fontWeight: 700,
    },
    h6: {
      color: '#000000',
      fontSize: '1.075rem',
      fontWeight: 400,
    },
    subtitle1: {
      color: '#000000',
      fontSize: '1rem',
      fontWeight: 400,
    },
    subtitle2: {
      color: '#616161',
      fontSize: '1rem',
      fontWeight: 400,
    },
    body1: {
      fontSize: '1rem', // 16px
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: '1.35rem',
      color: '#323232',
    },
    caption: {
      color: '#C0C0C0',
      fontSize: '0.875rem',
    },
  },

  // custom

  // to remove
  ...core,
  spacing: 4,
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Keep MUI autofill detection keyframes (label shrink); only delay the yellow bg flash.
          '&:-webkit-autofill': {
            animationDuration: '5000s',
            animationName: 'mui-auto-fill',
            transitionDelay: '9999999999s',
            transitionProperty: 'background-color, color',
          },
          '&:not(:-webkit-autofill)': {
            animationDuration: '5000s',
            animationName: 'mui-auto-fill-cancel',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#f5f5f5',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
        icon: createElement(CheckboxUncheckedIcon),
        checkedIcon: createElement(CheckboxCheckedIcon),
        indeterminateIcon: createElement(CheckboxIndeterminateIcon),
      },
      styleOverrides: {
        root: ({ theme }) => ({
          // Unchecked outline matches MUI outlined dropdowns (rgba(0,0,0,0.23)).
          '& svg rect': {
            stroke: alpha(theme.palette.text.primary, 0.23),
          },
          '&.Mui-checked svg rect, &.MuiCheckbox-indeterminate svg rect': {
            stroke: theme.palette.primary.main,
          },
          '&.Mui-disabled svg rect': {
            stroke: theme.palette.action.disabled,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        // v4 defaulted to "default" (neutral); v5 defaults to "primary"
        color: 'inherit',
      },
      styleOverrides: {
        contained: {
          boxShadow: blankTheme.shadows[1],
          transition: blankTheme.transitions.create(['box-shadow', 'background'], {
            easing: blankTheme.transitions.easing.easeOut,
            duration: blankTheme.transitions.duration.standard,
          }),
          '&:hover': {
            boxShadow: blankTheme.shadows[3],
          },
        },
        // Former v4 "default" contained look (white surface) — used when no color prop is set
        containedInherit: {
          backgroundColor: blankTheme.palette.common.white,
          color: blankTheme.palette.text.primary,
          '&:hover': {
            backgroundColor: blankTheme.palette.common.white,
            color: blankTheme.palette.text.primary,
          },
        },
        containedPrimary: {
          backgroundColor: '#464af0',
          color: blankTheme.palette.common.white,
          '&:hover': {
            backgroundColor: '#464af0',
            color: blankTheme.palette.common.white,
          },
        },
        containedSecondary: {
          backgroundColor: '#F2AF0D',
          color: blankTheme.palette.common.white,
          '&:hover': {
            backgroundColor: '#F2AF0D',
            color: blankTheme.palette.common.white,
          },
        },
        outlined: {
          backgroundColor: blankTheme.palette.common.white,
          color: '#464af0',
          '&:hover': {
            backgroundColor: blankTheme.palette.common.white,
            color: '#464af0',
          },
        },
        outlinedPrimary: {
          color: '#464af0',
          '&:hover': {
            color: '#464af0',
          },
        },
      },
    },
  },
} as any); // todo remove _core.js and fix types
